import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, Receipt, Printer } from "@phosphor-icons/react";
import api from "@/lib/api";

export default function Invoices() {
  const [rows, setRows] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/invoices");
        setRows(data);
      } catch {}
    })();
  }, []);

  const printInvoice = (inv) => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(renderInvoiceHTML(inv));
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div>
        <div className="text-xs uppercase tracking-widest font-bold text-stone-500">Financial</div>
        <div className="font-display text-2xl font-bold text-stone-900">GST-ready Invoices</div>
      </div>
      <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-widest font-bold">
            <tr>
              <th className="text-left p-3">Invoice #</th>
              <th className="text-left p-3">Commodity</th>
              <th className="text-left p-3">Buyer / Seller</th>
              <th className="text-right p-3">Subtotal</th>
              <th className="text-right p-3">GST (5%)</th>
              <th className="text-right p-3">Total</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows === null && Array.from({ length: 3 }).map((_, i) => (
              <tr key={i}><td colSpan={7} className="p-3"><div className="h-6 shimmer rounded" /></td></tr>
            ))}
            {rows && rows.length === 0 && (
              <tr><td colSpan={7} className="p-10 text-center text-stone-500">Invoices auto-generate when a deal is delivered.</td></tr>
            )}
            {rows && rows.map((inv, i) => (
              <motion.tr key={inv.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }} className="border-t border-stone-100 hover:bg-stone-50">
                <td className="p-3 font-mono text-xs font-bold text-stone-800">{inv.invoice_number}</td>
                <td className="p-3 text-stone-700">{inv.commodity} · {inv.quantity} {inv.unit}</td>
                <td className="p-3 text-xs">{inv.buyer_name} <span className="text-stone-400">←</span> {inv.seller_name}</td>
                <td className="p-3 text-right font-mono">₹{inv.subtotal.toLocaleString("en-IN")}</td>
                <td className="p-3 text-right font-mono text-orange-700">₹{inv.tax_amount.toLocaleString("en-IN")}</td>
                <td className="p-3 text-right font-mono font-bold">₹{inv.total.toLocaleString("en-IN")}</td>
                <td className="p-3 text-right space-x-2">
                  <button onClick={() => setPreview(inv)} className="text-xs font-bold text-emerald-800 hover:text-orange-600" data-testid={`preview-invoice-${inv.id}`}>Preview</button>
                  <button onClick={() => printInvoice(inv)} className="inline-flex items-center gap-1 text-xs font-bold text-white bg-orange-600 hover:bg-orange-700 px-2 py-1 rounded" data-testid={`print-invoice-${inv.id}`}>
                    <Printer size={12} /> PDF
                  </button>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setPreview(null)}>
          <div onClick={(e) => e.stopPropagation()} className="max-w-2xl w-full bg-white rounded-xl border border-stone-200 shadow-2xl overflow-hidden" data-testid="invoice-preview">
            <iframe title="invoice-preview" srcDoc={renderInvoiceHTML(preview)} className="w-full h-[80vh] border-0" />
          </div>
        </div>
      )}
    </div>
  );
}

function renderInvoiceHTML(inv) {
  return `<!doctype html><html><head><title>Invoice ${inv.invoice_number}</title>
  <style>
    body{font-family:-apple-system,Segoe UI,sans-serif;padding:36px;color:#1c1917;max-width:720px;margin:auto;}
    .head{display:flex;justify-content:space-between;align-items:start;border-bottom:2px solid #064E3B;padding-bottom:16px;}
    .brand{font-weight:900;font-size:26px;color:#064E3B;letter-spacing:-0.02em;}
    .sub{font-size:11px;letter-spacing:0.25em;color:#EA580C;text-transform:uppercase;}
    .meta{font-size:12px;color:#57534E;text-align:right;}
    table{width:100%;border-collapse:collapse;margin-top:24px;font-size:14px;}
    th{background:#F5F5F4;text-align:left;padding:10px;font-size:11px;letter-spacing:0.15em;text-transform:uppercase;}
    td{padding:12px 10px;border-bottom:1px solid #E7E5E4;}
    .totals{margin-top:24px;text-align:right;}
    .totals div{margin:4px 0;}
    .totals .grand{font-size:22px;font-weight:900;color:#064E3B;}
    .badge{display:inline-block;padding:3px 8px;background:#DCFCE7;color:#166534;border-radius:4px;font-size:10px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;margin-top:8px;}
    .foot{margin-top:36px;padding-top:16px;border-top:1px solid #E7E5E4;font-size:11px;color:#78716C;}
  </style></head><body>
    <div class="head">
      <div>
        <div class="sub">Tax Invoice</div>
        <div class="brand">INDIA MANDI</div>
        <div style="font-size:11px;color:#57534E">Mandi.GLCZone.in · A GLC Zone Initiative</div>
      </div>
      <div class="meta">
        <div><b>${inv.invoice_number}</b></div>
        <div>Date: ${new Date(inv.created_at).toLocaleDateString("en-IN")}</div>
        <div class="badge">GST @ ${inv.gst_rate || 5}%</div>
      </div>
    </div>
    <div style="margin-top:24px;display:flex;gap:24px;">
      <div style="flex:1"><div style="font-size:10px;text-transform:uppercase;letter-spacing:0.15em;color:#78716C">Seller</div><div style="font-weight:700;margin-top:2px">${inv.seller_name}</div></div>
      <div style="flex:1"><div style="font-size:10px;text-transform:uppercase;letter-spacing:0.15em;color:#78716C">Buyer</div><div style="font-weight:700;margin-top:2px">${inv.buyer_name}</div></div>
    </div>
    <table>
      <tr><th>Item</th><th>Qty</th><th>Rate</th><th style="text-align:right">Amount</th></tr>
      <tr><td>${inv.commodity} (Grade A)</td><td>${inv.quantity} ${inv.unit}</td><td>₹${inv.rate}</td><td style="text-align:right">₹${inv.subtotal.toLocaleString("en-IN")}</td></tr>
    </table>
    <div class="totals">
      <div>Subtotal: <b>₹${inv.subtotal.toLocaleString("en-IN")}</b></div>
      <div>GST @ ${inv.gst_rate || 5}%: <b>₹${inv.tax_amount.toLocaleString("en-IN")}</b></div>
      <div class="grand">Total: ₹${inv.total.toLocaleString("en-IN")}</div>
    </div>
    <div class="foot">Payment recorded via India Mandi. Auto-generated on verified delivery. For queries: hello@glczone.in</div>
  </body></html>`;
}
